import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR, track } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { MessageCircle, ShoppingBag, Leaf, Package, ShieldCheck, Sprout } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import VideoSection from "@/components/VideoSection";
import { openWhatsApp } from "@/lib/whatsapp";

export default function Home() {
  const { settings } = useSettings();
  const [product, setProduct] = useState(null);
  const navigate = useNavigate();
  const home = settings?.homepage || {};
  const brand = settings?.brand || {};

  useEffect(() => {
    api.get("/products").then(({ data }) => setProduct(data[0])).catch(() => {});
  }, []);

  const scrollToProducts = () => {
    const el = document.getElementById("products");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
          <div className="animate-fade-up">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-terracotta bg-wheat-pale px-3 py-1.5 rounded-full">
              {home.hero_badge || "Freshly Ground • From Hajipur, Bihar"}
            </span>
            <h1 className="mt-5 font-serif font-extrabold tracking-tight leading-[1.05] text-forest-dark text-5xl sm:text-6xl">
              {brand.name || "AREEBA"}
            </h1>
            <p className="mt-2 font-serif text-3xl sm:text-4xl text-wheat font-semibold">{home.hero_title || "Multigrain Aata"}</p>
            <p className="mt-3 text-xl italic text-forest/80 font-serif">{brand.tagline || "Har Roti Mein Tazgi."}</p>
            <p className="mt-5 text-charcoal/70 text-base max-w-md leading-relaxed">
              {home.hero_subtitle || "Freshly Ground Multigrain Aata, made for everyday family meals."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={scrollToProducts} data-testid="hero-shop-now-button" className="bg-forest hover:bg-forest-dark text-cream rounded-full h-12 px-7 text-base font-semibold">
                <ShoppingBag size={18} className="mr-2" /> Shop Now
              </Button>
              <Button onClick={() => openWhatsApp()} data-testid="hero-whatsapp-button" variant="outline" className="border-whatsapp text-whatsapp hover:bg-whatsapp/10 rounded-full h-12 px-7 text-base font-semibold">
                <MessageCircle size={18} className="mr-2" /> Order on WhatsApp
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-b from-white to-sand shadow-2xl shadow-forest/20 aspect-[4/5] max-w-md mx-auto flex items-center justify-center p-4">
              <img
                src="/brand/front.png"
                alt="Areeba Multigrain Aata 1 KG pack"
                fetchPriority="high"
                className="h-full w-full object-contain drop-shadow-xl"
              />
              <div className="absolute bottom-4 left-4 right-4 flex gap-2 justify-center">
                {["1 KG", "5 KG", "10 KG"].map((p) => (
                  <span key={p} className="bg-forest/90 backdrop-blur text-cream text-sm font-bold px-4 py-1.5 rounded-full">{p}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FRESHNESS USP */}
      <section id="freshness" className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-wheat text-xs font-semibold uppercase tracking-widest mb-3">Our Promise</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-forest-dark">{home.freshness_heading || "Freshly Ground. Carefully Packed."}</h2>
            <p className="text-charcoal/70 mt-4 text-base leading-relaxed" data-testid="freshness-text">{home.freshness_text}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Sprout, t: "Selected Grains", d: "Carefully chosen grains for every batch." },
              { icon: Leaf, t: "Small-Batch Grinding", d: "Ground in small batches with freshness in mind." },
              { icon: ShieldCheck, t: "Controlled Inventory", d: "Managed stock so aata stays fresh." },
              { icon: Package, t: "Careful Packing", d: "Hygienically packed and batch coded." },
            ].map((f) => (
              <div key={f.t} className="rounded-2xl bg-sand p-6 border border-forest/5">
                <f.icon className="text-forest" size={28} />
                <h3 className="font-bold text-lg text-forest-dark mt-4">{f.t}</h3>
                <p className="text-sm text-charcoal/65 mt-1.5">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="products" className="py-16 sm:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-wheat text-xs font-semibold uppercase tracking-widest mb-3">Areeba Multigrain Aata</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-forest-dark">Choose Your Pack</h2>
            <p className="text-charcoal/65 mt-3">Select the pack that fits your family. Prices include everyday value.</p>
          </div>
          {product ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                {product.variants.filter((v) => v.active !== false).map((v) => (
                  <ProductCard key={v.sku} product={product} variant={v} />
                ))}
              </div>
              <div className="text-center mt-10">
                <Button onClick={() => navigate(`/product/${product.slug}`)} variant="outline" data-testid="view-product-details" className="border-forest/30 text-forest rounded-full h-11 px-6 font-semibold">
                  View Full Product Details
                </Button>
              </div>
            </>
          ) : (
            <p className="text-center text-charcoal/50">Loading products…</p>
          )}
        </div>
      </section>

      <VideoSection />

      {/* STORY STRIP */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-3xl overflow-hidden aspect-[4/3] shadow-xl">
            <img src="https://images.unsplash.com/photo-1546833999-b9f581a1996d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Fresh rotis" className="h-full w-full object-cover" loading="lazy" />
          </div>
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-forest-dark">Made for everyday family meals</h2>
            <p className="text-charcoal/70 mt-4 leading-relaxed">{settings?.about?.story}</p>
            <Button onClick={() => navigate("/about")} className="mt-6 bg-forest text-cream rounded-full h-11 px-6" data-testid="home-about-button">Our Story</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
